import { Component, EventEmitter, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ForgotPasswordComponent } from '@auth';
import { regexHelper } from '@helpers';
import { AuthService, EnvService, EventBusService, EventType, SessionService } from '@services';
import {
  otpValidator,
  phoneDoesNotExistValidator,
  rateLimitValidator,
} from '../../../core/validators';

import { Country } from '@angular-material-extensions/select-country';
import { Subject, takeUntil, throttleTime } from 'rxjs';
import { countryUS } from '../../../core/helpers/countries';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent {
  @Output() loggined = new EventEmitter();
  public form: FormGroup;
  public loginError: string;
  public isPasswordVisible = false;
  public submitted = false;
  public authType = '';
  public isVerifyOtpVisible = false;
  public userPhoneNumber = '';
  public selectedCountry = countryUS;
  public otpDeadline: Date;
  public otpMinutes: number = 3;
  public isCountdownEnded = false;

  private otpClick$ = new Subject<void>();
  private verifyOtpClick$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private eventBusService: EventBusService,
    private sessionService: SessionService,
    private env: EnvService,
  ) {
    this.authType = this.env.environment.auth_type;

    const passwordAuthValidator =
      this.authType === 'password' ? Validators.required : Validators.nullValidator;
    const phoneAuthValidator =
      this.authType === 'phone' ? Validators.required : Validators.nullValidator;
    const otpAuthValidator =
      this.authType === 'phone' && this.isVerifyOtpVisible
        ? Validators.required
        : Validators.nullValidator;
    this.form = this.formBuilder.group({
      email: ['', [passwordAuthValidator, Validators.pattern(regexHelper.emailValidate())]],
      password: ['', [passwordAuthValidator]],
      phone: [
        '',
        [phoneAuthValidator, rateLimitValidator, Validators.pattern(regexHelper.phoneValidate())],
      ],
      otp: ['', [otpAuthValidator, Validators.pattern(regexHelper.otpValidate())]],
      country: [{ country: [countryUS] }],
    });

    this.otpClick$
      .pipe(takeUntil(this.destroy$), throttleTime(500))
      .subscribe(() => this.handleOtpRequest());

    this.verifyOtpClick$
      .pipe(takeUntil(this.destroy$), throttleTime(500))
      .subscribe(() => this.handleVerifyOtp());
  }

  public onCountrySelected(country: Country) {
    this.selectedCountry = country;
  }

  getErrorMessage(field: string) {
    switch (field) {
      case 'email':
        return this.authService.getControlError(this.form, field, ['required', 'pattern']);
      case 'phone':
        return this.authService.getControlError(this.form, field, [
          'required',
          'pattern',
          'phoneDoesNotExist',
          'blocked',
        ]);
      case 'otp':
        return this.authService.getControlError(this.form, field, ['pattern', 'invalid']);
      case 'password':
        return this.authService.getControlError(this.form, field, ['required']);
    }
  }

  enableForm() {
    this.submitted = false;
    this.form.enable();
  }

  // login() {
  //   const { email, password } = this.form.value;
  //   this.form.disable();
  //   this.submitted = true;
  //   this.authService.login(email, password).subscribe({
  //     next: (response) => {
  //       if (this.sessionService.accessToSite) {
  //         this.router.navigate(['/map']);
  //       }
  //       this.loggined.emit(response);
  //     },
  //     error: (err) => {
  //       this.enableForm();
  //       this.loginError = err.error.message;
  //     },
  //   });
  // }

  requestOtp() {
    this.form.disable();
    this.submitted = true;
    this.otpClick$.next();
  }

  handleOtpRequest() {
    const { phone } = this.form.value;
    this.form.disable();
    this.submitted = true;
    this.loginError = '';
    this.authService.requestOtp(phone, this.selectedCountry.numericCode, 'login').subscribe({
      next: (response: any) => {
        this.userPhoneNumber = response.phone_number;
        this.toggleOtpVerify(true);
        this.form.enable();
        this.submitted = false;
        this.otpDeadline = new Date(new Date().getTime() + this.otpMinutes * 60000);
        this.isCountdownEnded = false;
      },
      error: (err) => {
        this.loginError = err.error.message;
        this.enableForm();

        if (
          err.error.message ===
          `Please wait ${this.otpMinutes} minutes before requesting another code for the same phone number.`
        ) {
          this.checkRateLimitError(true);
          setTimeout(() => {
            this.checkRateLimitError(false);
          }, 4000);
        } else if (err.message) {
          this.checkPhoneExistsError(true);
          setTimeout(() => {
            this.checkPhoneExistsError(false);
          }, 4000);
        }
      },
    });
  }

  onOtpCountdownEnd() {
    this.isCountdownEnded = true;
  }

  verifyOtp() {
    this.form.disable();
    this.submitted = true;
    this.verifyOtpClick$.next();
  }

  handleVerifyOtp() {
    const { phone, otp } = this.form.value;
    this.loginError = '';
    this.authService.verifyLoginOtp(phone, this.selectedCountry.numericCode, otp).subscribe({
      next: (response) => {
        if (this.sessionService.accessToSite) {
          this.router.navigate(['/map']);
        }
        this.loggined.emit(response);
      },
      error: (err) => {
        this.enableForm();

        if (err.status === 400) {
          this.checkValidOtpError(true);
          setTimeout(() => {
            this.checkValidOtpError(false);
          }, 4000);
        } else {
          this.loginError = err.error.message;
        }
      },
    });
  }

  togglePasswordVisible() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  toggleOtpVerify(value: boolean) {
    this.isVerifyOtpVisible = value;
  }

  goBack() {
    this.form.controls['phone'].reset();
    this.toggleOtpVerify(false);
  }

  private checkRateLimitError(blocked: boolean) {
    this.setFieldsValidators([this.form.controls['phone']], [rateLimitValidator(blocked)]);
    this.getErrorMessage('phone');
  }

  private checkPhoneExistsError(isExists: boolean) {
    this.setFieldsValidators([this.form.controls['phone']], [phoneDoesNotExistValidator(isExists)]);
    this.getErrorMessage('phone');
  }

  private checkValidOtpError(invalid: boolean) {
    this.setFieldsValidators([this.form.controls['otp']], [otpValidator(invalid)]);
    this.getErrorMessage('otp');
  }

  public async restorePassword(): Promise<void> {
    this.dialog.closeAll();
    const dialogRef = this.dialog.open(ForgotPasswordComponent, {
      width: '100%',
      maxWidth: 576,
      panelClass: ['modal', 'forgot-password-modal'],
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate([`/reset`]);
      } else {
        this.eventBusService.next({
          type: EventType.OpenLoginModal,
          payload: {},
        });
      }
    });
  }
  private setFieldsValidators(controls: AbstractControl[], validators: ValidatorFn[]): void {
    controls.map((control) => {
      control.setValidators(validators);
      control.updateValueAndValidity();
    });
  }
}
